import { useState, type FormEvent } from 'react'
import { Plus, X } from 'lucide-react'
import { useSocialStore } from '@/store/social'
import { describeError } from '@/lib/auth'
import type { Emoji } from '@/types/api'
import { Modal } from '../settings/Modal'
import { EmojiButton, type PickedEmoji } from './EmojiButton'
import auth from '@/pages/AuthPage.module.css'
import styles from './CreatePollModal.module.css'
import { Select } from '../Select'

const MAX_ANSWERS = 10
const DURATIONS: { hours: number; label: string }[] = [
  { hours: 1, label: '1 час' },
  { hours: 4, label: '4 часа' },
  { hours: 8, label: '8 часов' },
  { hours: 24, label: '24 часа' },
  { hours: 72, label: '3 дня' },
  { hours: 168, label: '1 неделя' },
  { hours: 336, label: '2 недели' },
]

interface Answer {
  key: number
  text: string
  emoji: PickedEmoji | null
}

/** Создание опроса: вопрос, от двух до десяти вариантов с эмодзи, срок и один или несколько голосов. */
export function CreatePollModal({ channelId, custom, onClose }: { channelId: string; custom?: { guild: string; emojis: Emoji[] }[] | undefined; onClose: () => void }) {
  const sendPoll = useSocialStore((s) => s.sendPoll)
  const [question, setQuestion] = useState('')
  const [answers, setAnswers] = useState<Answer[]>([{ key: 1, text: '', emoji: null }, { key: 2, text: '', emoji: null }])
  const [nextKey, setNextKey] = useState(3)
  const [hours, setHours] = useState(24)
  const [multi, setMulti] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filled = answers.filter((a) => a.text.trim())
  const ready = question.trim().length > 0 && filled.length >= 2

  const update = (key: number, patch: Partial<Answer>) => setAnswers((list) => list.map((a) => (a.key === key ? { ...a, ...patch } : a)))
  const add = () => {
    if (answers.length >= MAX_ANSWERS) return
    setAnswers((list) => [...list, { key: nextKey, text: '', emoji: null }])
    setNextKey((k) => k + 1)
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!ready || busy) return
    setBusy(true)
    setError(null)
    try {
      await sendPoll(channelId, {
        question: { text: question.trim() },
        answers: filled.map((a) => ({ poll_media: { text: a.text.trim(), ...(a.emoji ? { emoji: a.emoji } : {}) } })),
        duration: hours,
        allow_multiselect: multi,
      })
      onClose()
    } catch (err) {
      const d = describeError(err)
      setError(Object.values(d.fields)[0] ?? d.message)
      setBusy(false)
    }
  }

  return (
    <Modal title="Создать опрос" onClose={onClose} width={500}>
      <form className={styles.form} onSubmit={(e) => void submit(e)}>
        <label className={styles.field}>
          <span className={styles.label}>Вопрос</span>
          <input className={auth.input} value={question} maxLength={300} autoFocus placeholder="О чём спросить?" onChange={(e) => setQuestion(e.target.value)} />
        </label>
        <div className={styles.field}>
          <span className={styles.label}>Варианты</span>
          <ol className={styles.answers}>
            {answers.map((a, i) => (
              <li key={a.key} className={styles.answer}>
                <EmojiButton value={a.emoji} onChange={(emoji) => update(a.key, { emoji })} custom={custom} label={`Эмодзи варианта ${i + 1}`} />
                <input className={auth.input} value={a.text} maxLength={55} placeholder={`Вариант ${i + 1}`} aria-label={`Вариант ${i + 1}`} onChange={(e) => update(a.key, { text: e.target.value })} />
                {answers.length > 2 ? (
                  <button type="button" className={styles.remove} aria-label={`Убрать вариант ${i + 1}`} data-tooltip="Убрать вариант" onClick={() => setAnswers((list) => list.filter((x) => x.key !== a.key))}>
                    <X size={16} />
                  </button>
                ) : null}
              </li>
            ))}
          </ol>
          {answers.length < MAX_ANSWERS ? (
            <button type="button" className={styles.add} onClick={add}>
              <Plus size={16} aria-hidden="true" /> Добавить вариант
            </button>
          ) : null}
        </div>
        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>Длительность</span>
            <Select value={hours} options={DURATIONS.map((d) => ({ value: d.hours, label: d.label }))} onChange={setHours} />
          </label>
          <label className={styles.check}>
            <input type="checkbox" checked={multi} onChange={(e) => setMulti(e.target.checked)} />
            <span>Можно выбрать несколько</span>
          </label>
        </div>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        <div className={styles.buttons}>
          <button type="button" className={styles.cancel} onClick={onClose}>Отмена</button>
          <button type="submit" className={styles.primary} disabled={!ready || busy}>{busy ? 'Отправляем…' : 'Отправить'}</button>
        </div>
      </form>
    </Modal>
  )
}
